import { FC, useEffect, useRef, useState, UIEvent } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { axiosInstance } from '../../../../services/axios';
import { API_SLUGS, ROUTES } from '../../../../constants';
import { Box, Button, Card, Checkbox, Chip, Container, IconButton, styled, Switch, Tooltip, Typography } from '@mui/material';
import MaterialReactTable from 'material-react-table';
import { IAlertState, useAlertState } from '../../../../store/alertState';
import ModalWrapper from '../../../common/modal/wrapper';
import LinkCloudProjectForm from './LinkCloudProjectForm';
import { useTableInfinity } from '../../../../hooks/process/useTableInfinity';
import { InfoOutlined, PlayCircle } from '@mui/icons-material';
// import { GitlabScannersPopup } from './GitlabScannersPopUp';
import CloudRunnerConfig from '../../../dashboard/CloudRunnerConfig';
import { useLocation } from "react-router-dom";
import { startCase } from 'lodash';

const limit = 30;
const disableInfiniteScroll = false;

enum Flags {
    AI_CODE_REVIEW = "aptoriVcsAiCodeReview",
    AI_ASSIST = "aptoriVcsAiAssist",
    SCAN_ON_MR = "aptoriVcsScanOnMr",
    SCAN_ON_DEMAND = "aptoriVcsScanOnDemand"
}

const CloudConfigurationForm: FC<any> = () => {
    const [checkboxStates, setCheckboxStates] = useState<any>([]);
    const [linkedAssets, setLinkedAssets] = useState<any>([]);
    const { setMessage } = useAlertState(state => state) as IAlertState
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const type = params.get("type");
    const navigate = useHistory();
    const path = API_SLUGS.CLOUD_INTEGRATION_PROJECTS + `?type=${type}&limit={limit}&offset={offset}`
    const tableContainerRef = useRef<HTMLDivElement>(null);
    


    const { rows, fetchMoreOnBottomReached, isFetching, setRows } = useTableInfinity(
        "projects",
        path,
        tableContainerRef,
        limit
    );

    useEffect(() => {
        if (!disableInfiniteScroll) {
            fetchMoreOnBottomReached(tableContainerRef.current);
        }
    }, [fetchMoreOnBottomReached])

    useEffect(() => {
        const updatedStates = rows.map(row => ({
            [Flags.AI_CODE_REVIEW] : row?.details?.[Flags.AI_CODE_REVIEW],
            [Flags.AI_ASSIST] : row?.details?.[Flags.AI_ASSIST],
            [Flags.SCAN_ON_MR] : row?.details?.[Flags.SCAN_ON_MR],
            [Flags.SCAN_ON_DEMAND] : row?.details?.[Flags.SCAN_ON_DEMAND]
        }));
        setCheckboxStates(updatedStates);
    }, [rows]);


    const handleCheckboxChange = async (index: number, value: boolean, projectId: string, field: Flags) => {
        setCheckboxStates((prev: any) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    
        const details: any = { projectId };
        details[field] = value;
    
        const payload = {
            type: type,
            details
        };
    
        try {
            const response = await axiosInstance.put(API_SLUGS.CLOUD_INTEGRATION_PROJECTS, payload);
            const updatedDetails = response.data.details;
    
          
            setRows((prev) => {
                const updated = [...prev];
                const existing = updated[index];
    
                updated[index] = {
                    ...existing,
                    details: {
                        ...existing.details,
                        isWebhookActive: updatedDetails?.isWebhookActive,
                        [Flags.AI_CODE_REVIEW] : updatedDetails?.[Flags.AI_CODE_REVIEW],
                        [Flags.AI_ASSIST] : updatedDetails?.[Flags.AI_ASSIST],
                        [Flags.SCAN_ON_MR] : updatedDetails?.[Flags.SCAN_ON_MR],
                        [Flags.SCAN_ON_DEMAND] : updatedDetails?.[Flags.SCAN_ON_DEMAND]
                    }
                };
    
                return updated;
            });
    
            setCheckboxStates((prev: any) => {
                const updated = [...prev];
                updated[index] = { ...updated[index], [field]: value };
                return updated;
            });
    
            setMessage({
                title: "Gitlab project configuration updated",
                type: "success"
            });
        } catch (error) {
            console.log(error);
        }
    };
    
      

    return (
        <Box sx={{ backgroundColor: "background.paper", display: "flex", minHeight: '100%' }}>
            <Container maxWidth={false} sx={{ p: 3, pt: 1.5 }}>
                <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h4" color="textPrimary" gutterBottom>
                        {startCase(type || "")} Configuration 
                    </Typography>
                </Box>
                <Card elevation={0}>
                    <MaterialReactTable
                        muiTablePaperProps={{ elevation: 0 }}
                        columns={[
                            {
                                accessorKey: "name",
                                header: `${startCase(type || "")} Project`,
                                size: 200
                            },
                           
                            {
                                accessorKey: 'aptori_project',
                                header: 'Aptori Asset',
                                Cell: ({ row }) => {
                                    const index = row?.index
                                    return (
                                        <ModalWrapper
                                            maxWidth="lg"
                                            title={`Link Aptori Asset`}
                                            trigger={
                                                rows?.[index]?.details?.assets?.length > 0 ? (
                                                <Button size="small" variant="contained" disabled={isFetching}>
                                                    {rows?.[index]?.details?.assets?.map((asset: any) => asset?.name).join(', ')}
                                                </Button>) :
                                                    (<Button size="small" variant="outlined" disabled={isFetching}>
                                                        {"Link to Aptori Asset"}
                                                    </Button>)

                                            }
                                            child={
                                                <LinkCloudProjectForm
                                                    projectName={row?.original?.name}
                                                    cloudProjectId={rows?.[index]?.id}
                                                    selectedAssets={rows?.[index]?.details?.assets}
                                                    selectedBranchesData={rows?.[index]?.details?.branches}
                                                    callback={() => navigate.push(`${ROUTES.CLOUD_CONFIGURATION}?type=${type}`)}
                                                    type={type}
                                                />
                                            }
                                        />
                                    );
                                },
                            },
                            {
                                accessorKey: "run",
                                header: "Run",
                                Cell: ({ row }) => {
                                    const index = row?.index
                                    return (<CloudRunnerConfig  type={type} presetRepo={rows?.[index]} trigger={<Tooltip title="Analyze" ><IconButton disabled={!rows?.[index]?.details?.assets?.length || rows?.[index]?.details?.assets?.length <= 0 || isFetching}><PlayCircle /></IconButton></Tooltip>} />)
                                    //return (<GitlabScannersPopup  repoData={rows?.[index]} trigger={<Tooltip title="Analyze"><IconButton disabled={!rows?.[index]?.details?.assets?.length || rows?.[index]?.details?.assets?.length <= 0 || isFetching}><PlayCircle /></IconButton></Tooltip>} />)
                                },
                                size: 50
                            },
                            {
                                accessorKey: 'aptoriVcsAiCodeReview',
                                header: "AI Code Review",
                                Cell: ({ row }) => {
                                    const index = row.index;
                                    return (
                                        <Checkbox
                                            size="medium"
                                            disabled={!rows?.[index]?.details?.assets?.length || rows?.[index]?.details?.assets?.length <= 0 || isFetching}
                                            checked={checkboxStates[index]?.[Flags.AI_CODE_REVIEW] || false}
                                            onChange={({ target }) =>
                                                handleCheckboxChange(index, target.checked, rows?.[index]?.id, Flags.AI_CODE_REVIEW)
                                            }
                                        />
                                    );
                                },
                            },
                            {
                                accessorKey: 'aptoriVcsAiAssist',
                                header: "AI Assist",
                                Cell: ({ row }) => {
                                    const index = row.index;
                                    return (
                                        <Checkbox
                                            size="medium"
                                            disabled={!rows?.[index]?.details?.assets?.length || rows?.[index]?.details?.assets?.length <= 0 || isFetching}
                                            checked={checkboxStates[index]?.[Flags.AI_ASSIST] || false}
                                            onChange={({ target }) =>
                                                handleCheckboxChange(index, target.checked, rows?.[index]?.id, Flags.AI_ASSIST)
                                            }
                                        />
                                    );
                                },
                            },
                            {
                                accessorKey: 'aptoriVcsScanOnMr',
                                header: "Scan on MR",
                                // Header:() => (
                                //     <Box display="flex" alignItems="center">
                                //        Scan on Cloud
                                //         <Tooltip title="When enabled, it runs a scan on PR for selected branch.">
                                //         <InfoOutlined sx={{ ml: 0.5 }} fontSize="inherit" className="hand" />
                                //         </Tooltip>
                                //     </Box>
                                //   ),
                                Cell: ({ row }) => {
                                    const index = row.index;
                                    return (
                                        <Checkbox
                                            size="medium"
                                            disabled={!rows?.[index]?.details?.assets?.length || rows?.[index]?.details?.assets?.length <= 0 || isFetching}
                                            checked={checkboxStates[index]?.[Flags.SCAN_ON_MR] || false}
                                            onChange={({ target }) =>
                                                handleCheckboxChange(index, target.checked, rows?.[index]?.id, Flags.SCAN_ON_MR)
                                            }
                                        />
                                    );
                                },
                            },
                            {
                                accessorKey: 'isWebhookActive',
                                header: 'Webhook Status',
                                Cell: ({ row }) => {
                                    const index = row.index;
                                    const isActive = rows?.[index]?.details?.isWebhookActive
                                  
                                    return (
                                      <Typography
                                        color={isActive ? 'secondary' : 'error'}
                                        variant="h6"
                                      >
                                        {isActive ? 'Active' : 'Inactive'}
                                      </Typography>
                                    );
                                  }
                              }

                        ]}
                        data={rows}
                        enableGlobalFilter={false}
                        enableColumnActions={false}
                        initialState={{
                            density: "compact",
                            columnPinning: { left: ['name'] },
                        }}
                        muiTableContainerProps={{
                            ref: tableContainerRef, //get access to the table container element
                            sx: { maxHeight: "calc(100vh - 270px)" }, //give the table a max height
                            onScroll: (
                                event: UIEvent<HTMLDivElement> //add an event listener to the table container element
                            ) => {
                                if (!disableInfiniteScroll) {
                                    fetchMoreOnBottomReached(event.target as HTMLDivElement)
                                }
                            }

                        }}
                       
                        enableSorting={false}
                        enableStickyHeader
                        enableTopToolbar={false}
                        enablePagination={false}
                        enableDensityToggle={false}
                        enableColumnFilters={false}
                        state={{ showProgressBars: isFetching,  columnVisibility: { isWebhookActive: type==='gitlab' }  }}
                    />
                </Card>
            </Container>
        </Box>
    );
};


export default withRouter(CloudConfigurationForm);
